import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';

function Creators() {
  useSEO({
    title: 'Game Creators | Nightfall',
    description: 'Discover talented game creators and developers on Nightfall. Browse games from your favorite indie developers.',
    url: 'https://nightfallshop.com/creators'
  });
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API_URL}/creators`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Server error');
      }
      const data = await response.json();
      setCreators(data);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'TypeError' || err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
        setError('server_unavailable');
      } else {
        setError('server_error');
      }
      console.error('Error fetching creators:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col">
        <div className="font-sans text-base lg:text-lg text-white text-center py-10">Loading creators...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-12 max-w-md text-center mx-2">
          <svg className="w-16 h-16 mx-auto mb-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="font-semibold text-2xl text-white mb-3">
            {error === 'server_unavailable' ? 'Server Unavailable' : 'Server Error'}
          </h2>
          <p className="font-normal text-base text-white/60 mb-6">
            {error === 'server_unavailable' 
              ? "Sorry, we're having trouble connecting to the server. Please make sure the backend is running."
              : "Sorry, something went wrong on our end. Please try again later."}
          </p>
          <button
            onClick={fetchCreators}
            className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .creators-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .creators-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>
      <div className="creators-align mb-6 lg:mb-8">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Creators</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 lg:gap-6 w-full max-w-full">
        {creators.map(creator => (
          <div
            key={creator.id}
            onClick={() => {
              window.scrollTo(0, 0);
              navigate(`/creator/${creator.id}`);
            }}
            onContextMenu={(e) => e.preventDefault()}
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl md:rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-white/20 group min-w-0 cursor-pointer select-none"
          >
            {/* Profile Picture Section */}
            <div className="w-full aspect-square bg-gradient-to-br from-dark-border via-dark-card to-dark-surface flex items-center justify-center relative overflow-hidden select-none">
              {creator.pfp ? (
                <img
                  src={creator.pfp}
                  alt={creator.name}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 select-none"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-br from-dark-card/80 to-dark-surface/60 flex items-center justify-center" style={{ display: creator.pfp ? 'none' : 'flex' }}>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-accent-blue/20 border-2 border-accent-blue/40 flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl font-bold text-accent-blue/60">{creator.name.charAt(0)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-3 md:p-6 flex flex-col gap-2 md:gap-4">
              <div className="text-center">
                <h3 className="text-sm md:text-xl font-semibold text-white mb-0.5 md:mb-1">{creator.name}</h3>
                <p className="text-xs md:text-sm text-white/50">Game Developer</p>
              </div>

              <div className="border-t border-dark-border pt-2 md:pt-4">
                <div className="flex flex-col gap-1.5 md:gap-2.5">
                  {creator.itchLink && (
                    <a
                      href={creator.itchLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 md:gap-3 px-2 md:px-4 py-2 md:py-3 bg-dark-bg/50 border border-dark-border rounded-lg md:rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                    >
                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                        <img src="/images/itch_logo.png" alt="Itch.io" className="w-4 h-4 md:w-5 md:h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                      </div>
                      <span className="text-xs md:text-sm font-medium flex-1">Itch.io</span>
                      <svg className="w-3 h-3 md:w-4 md:h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                  {creator.patreonLink ? (
                    <a
                      href={creator.patreonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 md:gap-3 px-2 md:px-4 py-2 md:py-3 bg-dark-bg/50 border border-dark-border rounded-lg md:rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                    >
                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                        <img src="/images/patreon_logo.png" alt="Patreon" className="w-4 h-4 md:w-5 md:h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                      </div>
                      <span className="text-xs md:text-sm font-medium flex-1">Patreon</span>
                      <svg className="w-3 h-3 md:w-4 md:h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : creator.subscribestarLink ? (
                    <a
                      href={creator.subscribestarLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2 md:gap-3 px-2 md:px-4 py-2 md:py-3 bg-dark-bg/50 border border-dark-border rounded-lg md:rounded-xl text-white/70 hover:text-white hover:bg-dark-bg/70 hover:border-accent-blue/40 transition-all duration-200 group/link"
                    >
                      <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center group-hover/link:bg-accent-blue/20 group-hover/link:border-accent-blue/30 transition-colors">
                        <img src="/images/subscribestar_logo.png" alt="SubscribeStar" className="w-4 h-4 md:w-5 md:h-5 object-contain" draggable={false} onDragStart={(e) => e.preventDefault()} />
                      </div>
                      <span className="text-xs md:text-sm font-medium flex-1">SubscribeStar</span>
                      <svg className="w-3 h-3 md:w-4 md:h-4 opacity-50 group-hover/link:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

export default Creators;

